import React, { Suspense, useEffect } from 'react';
import AccordionMenu from './Components/Navbar';
import Footer from './Components/Footer';
import './App.css';
import { Routes, Route, Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

// Lazy load components
const About = React.lazy(() => import('./Components/About/About'));
const AboutMe = React.lazy(() => import('./Components/AboutMe/AboutMe'));
const Portfolio = React.lazy(() => import('./Components/Portfolio/Portfolio'));
const Apps = React.lazy(() => import('./Components/Apps/Apps'));
const ThisSite = React.lazy(() => import('./Components/ThisSite/ThisSite'));
const TapTempoPage = React.lazy(() => import('./Components/Apps/TapTempoPage'));
const HeatTrackerPage = React.lazy(() => import('./Components/HeatTracker/HeatTrackerPage'));
const Contact = React.lazy(() => import('./Components/Contact/Contact'));
const ThankYou = React.lazy(() => import('./Components/Contact/thank-you'));

function NotFound() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '12px' }}>
      <h1 style={{ fontSize: '1.4rem', fontWeight: 'normal', margin: 0 }}>page not found</h1>
      <Link to="/">back home</Link>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Per-page title + description so each page shows up distinctly in search
const PAGE_META = {
  '/': {
    title: 'Dalton Allison - Mixing & Mastering Engineer, Producer',
    description:
      'Dalton Allison is a Brooklyn-based mixing and mastering engineer, producer, and performer with Chicago band Post Animal. Browse credits and get in touch.',
  },
  '/aboutme': {
    title: 'About Dalton Allison - Engineer, Producer, Post Animal',
    description:
      'Dalton Allison (he/him) is a performer and producer with Chicago band Post Animal, and a mixing and mastering engineer based in NYC.',
  },
  '/credits': {
    title: 'Credits - Dalton Allison Mixing, Mastering & Production',
    description:
      'Selected releases mixed, mastered, produced, and performed on by Dalton Allison, including Post Animal, Djo, and more.',
  },
  '/apps': {
    title: 'Apps - Dalton Allison',
    description: 'Audio tools and apps built by Dalton Allison, including HEATTRACKER and Tap Tempo.',
  },
  '/taptempo': {
    title: 'Tap Tempo - Find a BPM by Tapping | Dalton Allison',
    description: 'A simple tap tempo tool: tap along to a beat and read the tempo in BPM.',
  },
  '/heattracker': {
    title: 'HEATTRACKER - Stereo Heat Map Analyser by Dalton Allison',
    description:
      'HEATTRACKER is a real-time stereo field and frequency heat map that runs in the browser. Play a demo, drop in an audio file, or analyse a tab or audio input.',
  },
  '/this-site': {
    title: 'About This Site - Dalton Allison',
    description:
      'How the Dalton Allison portfolio was designed and built with React and Three.js.',
  },
  '/contact': {
    title: 'Contact - Dalton Allison',
    description:
      'Get in touch with Dalton Allison about mixing, mastering, production, or session work.',
  },
};

function PageMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    const key = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
    // Pages without an entry (e.g. /this-site) get the site-wide default title
    const known = Boolean(PAGE_META[key]);
    const meta = PAGE_META[key] || PAGE_META['/'];

    // Keep the thank-you page and unknown URLs out of search results
    let robots = document.querySelector('meta[name="robots"]');
    if (!known && key !== '/') {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex');
    } else if (robots) {
      robots.remove();
    }

    document.title = meta.title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', meta.description);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute(
        'href',
        'https://daltonallison.com' + (key === '/' ? '/' : key)
      );
    }
  }, [pathname]);

  return null;
}

function App() {
  return (
    <>
      <AccordionMenu />

      <Footer />

      <ScrollToTop />

      <PageMeta />

      <Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}></div>}>
        <Routes>
          <Route path="/" element={<About />} />
          <Route path="/aboutme" element={<AboutMe />} />
          <Route path="/credits" element={<Portfolio />} />
          <Route path="/apps" element={<Apps />} />
          <Route path="/taptempo" element={<TapTempoPage />} />
          <Route path="/heattracker" element={<HeatTrackerPage />} />
          <Route path="/this-site" element={<ThisSite />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
